
import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import ServiceCard from "@/components/ServiceCard";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { Provider } from "@/types/supabase";
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import { cn } from "@/lib/utils";
import { getFeaturedProviders } from "@/utils/supabase/providerHelpers";
import { useTranslation } from "react-i18next";
import { trackHomepageEvent } from "@/services/homepageAnalytics";

const FeaturedProviders = () => {
  const { t } = useTranslation('homepage');
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const isMobile = useIsMobile();
  // Auto-scroll interval: 6 seconds per design spec
  const autoScrollInterval = 6000; 
  const intervalRef = useRef<number | null>(null);
  
  const limit = 12;
  const itemsPerPage = isMobile ? 1 : 4;
  // Calculate total pages based on actual providers length, not the limit
  const totalPages = Math.ceil(providers.length / itemsPerPage);
  
  // Handle auto-scrolling
  useEffect(() => {
    if (providers.length > 0 && totalPages > 1) {
      // Clear any existing interval when component updates
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
      }
      
      // Set up auto-scrolling interval
      intervalRef.current = window.setInterval(() => {
        setCurrentPage((prev) => (prev + 1) % totalPages);
      }, autoScrollInterval);
      
      // Clean up on component unmount
      return () => {
        if (intervalRef.current) {
          window.clearInterval(intervalRef.current);
        }
      };
    }
  }, [providers, totalPages]);

  useEffect(() => {
    setCurrentPage(0);
  }, [itemsPerPage]);
  
  useEffect(() => {
    const fetchFeaturedProviders = async () => {
      try {
        setLoading(true);
        // Use the getFeaturedProviders utility function to get featured providers
        const featuredProviders = await getFeaturedProviders(limit);
        setProviders(featuredProviders || []);
      } catch (error) {
        console.error("Error fetching featured providers:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchFeaturedProviders();
  }, []);

  // Pause auto-scrolling when user interacts with pagination
  const handlePageClick = (pageIndex: number) => {
    if (totalPages <= 1) {
      return;
    }

    // Clear any existing interval when user manually changes page
    if (intervalRef.current) {
      window.clearInterval(intervalRef.current);
    }
    
    setCurrentPage(pageIndex);
    
    // Resume auto-scrolling after a short delay
    setTimeout(() => {
      intervalRef.current = window.setInterval(() => {
        setCurrentPage((prev) => (prev + 1) % totalPages);
      }, autoScrollInterval);
    }, autoScrollInterval * 2); // Wait twice the interval before resuming
  };

  if (loading) {
    return <section className="py-8 md:py-16" id="featured-providers">
        <div className="container mx-auto px-3 md:px-4">
          <div className="mb-6 md:mb-8">
            <h2 className="font-heading text-[length:var(--text-h2)] font-semibold text-brand-ink">{t('featured.title')}</h2>
            <p className="mt-2 max-w-2xl text-[length:var(--text-body)] text-brand-slate">{t('featured.description')}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {[...Array(isMobile ? 1 : itemsPerPage)].map((_, i) => <div key={i} className="h-72 animate-pulse rounded-2xl bg-brand-cloud/80"></div>)}
          </div>
        </div>
      </section>;
  }
  
  if (providers.length === 0) {
    return (
      <section className="py-8 md:py-16" id="featured-providers">
        <div className="container mx-auto px-3 sm:px-4">
          <div className="mb-6 md:mb-8">
            <h2 className="font-heading text-[length:var(--text-h2)] font-semibold text-brand-ink">{t('featured.title')}</h2>
            <p className="mt-2 max-w-2xl text-[length:var(--text-body)] text-brand-slate">{t('featured.description')}</p>
          </div>
          <div className="rounded-2xl border border-brand-cloud bg-brand-mist/50 px-6 py-8 text-center text-brand-slate">
            {t('featured.empty')}
          </div>
        </div>
      </section>
    );
  }
  
  // Get current items
  const currentProviders = providers.slice(
    currentPage * itemsPerPage, 
    (currentPage + 1) * itemsPerPage
  );
  
  return (
    <section className="py-8 md:py-16" data-test-responsive="featured-providers" id="featured-providers">
      <div className="container mx-auto px-3 sm:px-4">
        <div className="mb-6 flex flex-col gap-3 md:mb-8 md:flex-row md:items-end md:justify-between">
          <div>
            <h2 className="font-heading text-[length:var(--text-h2)] font-semibold text-brand-ink">{t('featured.title')}</h2>
            <p className="mt-2 max-w-2xl text-[length:var(--text-body)] text-brand-slate">{t('featured.description')}</p>
          </div>
          <Link to="/explore?featured=true">
            <Button variant="outline" className="border-brand-ink text-brand-ink hover:bg-brand-ink hover:text-white transition-colors">
              {t('featured.viewAll')}
            </Button>
          </Link>
        </div>
        
        <Carousel className="w-full overflow-hidden">
          <CarouselContent className="-ml-2 md:-ml-4 pr-2 md:pr-4">
            {currentProviders.map((provider, index) => (
              <CarouselItem key={provider.id} className="pl-2 md:pl-4 basis-full sm:basis-1/2 lg:basis-1/4">
                <ServiceCard 
                  id={provider.id} 
                  name={provider.name} 
                  image={provider.image_url} 
                  categories={provider.categories} 
                  description={provider.description} 
                  rating={provider.rating} 
                  user_id={provider.user_id}
                  isVerified={provider.is_verified}
                  isFeatured={provider.is_featured}
                  suburbs={provider.suburbs}
                  pageType="home"
                  userProfileVerified={provider.user_profile_verified}
                  whatsapp_number={provider.whatsapp_number}
                  onProviderClick={() => trackHomepageEvent('homepage_featured_provider_click', {
                    provider_id: provider.id,
                    provider_name: provider.name,
                    position: currentPage * itemsPerPage + index + 1,
                  })}
                  onWhatsAppClick={() => trackHomepageEvent('homepage_whatsapp_click', {
                    provider_id: provider.id,
                    provider_name: provider.name,
                  })}
                />
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
        
        {/* Pagination dots - only show if there are multiple pages */}
        {totalPages > 1 && (
          <div className="flex justify-center mt-6" data-test-responsive="featured-providers-pagination">
            {Array.from({ length: totalPages }).map((_, index) => (
              <button
                key={index}
                className={cn(
                  "mx-1 w-2 h-2 rounded-full transition-all duration-300",
                  currentPage === index ? "bg-brand-ink w-4" : "bg-brand-cloud hover:bg-brand-slate"
                )}
                onClick={() => handlePageClick(index)}
                aria-label={`Go to page ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default FeaturedProviders;
